import { AxiosResponse } from 'axios';
import { getEntry, putEntry } from './db';
import { AxiosResponseOk } from './http-responses';
import { connection } from './internet';
import { session } from './session';


interface CacheEntry {
  keyPath: string;
  accountId: string;
  args: Array<string>;
  result: any;
}

export class OfflineCacheMissError extends Error {
  constructor(objectStore: string, args: unknown[]) {
    super(`No cached data for offline request to ${objectStore} (${JSON.stringify(args)})`);
    this.name = 'OfflineCacheMissError';
  }
}

async function getKeypath(args) {
  if (!session.account) throw new OfflineCacheMissError('session', args);
  return [
    session.account.id,
    ...args,
  ].map(e => e.toString()).join('@');
}

async function cacheResult(objectStore, args, result) {
  if (!session.account) return;
  await putEntry<CacheEntry>(objectStore, {
    keyPath: await getKeypath(args),
    args: args,
    accountId: session.account.id,
    result,
  });
}

async function retrieveCachedEntry(objectStore, args) {
  if (!session.account) return undefined;
  return await getEntry<CacheEntry>(objectStore, await getKeypath(args));
}

async function getRemoteOrLocal<T = any>(
  objectStore: string,
  remoteFn: (...remoteArgs: any[]) => Promise<AxiosResponse<T> | undefined>,
  ...rest: any[]
): Promise<AxiosResponse<T>> {
  // The final argument may be an `onFresh` callback. Whenever we serve a cached
  // payload up front (cache-first), we revalidate against the server in the
  // background and hand the fresh response to this callback so reactive callers
  // can re-render once it arrives. Detected by type, so the plain string/number/
  // array args of every other caller keep working unchanged.
  let onFresh: ((result: AxiosResponse<T>) => void) | undefined;
  let args = rest;
  if (typeof rest[rest.length - 1] === 'function') {
    onFresh = rest[rest.length - 1];
    args = rest.slice(0, -1);
  } else if (rest.length > 0 && rest[rest.length - 1] === undefined) {
    // A caller forwarding an unset onFresh (`…, this.onFresh)` with onFresh
    // undefined). Drop the trailing undefined so it never lands in `args` and
    // corrupts the cache key — getKeypath stringifies every arg.
    args = rest.slice(0, -1);
  }

  // Fetch from the server and persist the payload. Resolves to undefined when
  // we're (or just went) offline — the axios interceptor turns a "Network
  // Error" into undefined — so the caller can fall back to the cache.
  const revalidate = async (): Promise<AxiosResponse<T> | undefined> => {
    if (!connection.isConnected.value) return undefined;
    const result = await remoteFn(...args) as AxiosResponse<T> | undefined;
    if (result) await cacheResult(objectStore, args, result.data);
    return result;
  };

  const cacheEntry = await retrieveCachedEntry(objectStore, args);

  if (cacheEntry) {
    // Cache-first: never block the UI on a request that may be slow or doomed
    // (offline). Serve the persisted payload now, revalidate in the background,
    // and let reactive views update via onFresh when the server responds.
    revalidate()
      .then(fresh => { if (fresh && onFresh) onFresh(fresh); })
      .catch(() => undefined);
    return new AxiosResponseOk(cacheEntry.result);
  }

  // Nothing cached yet (e.g. first-ever load on this device): the server is the
  // only source, so we have to wait for it.
  const fresh = await revalidate();
  if (fresh) return fresh;

  // Offline (or a failed request) with no cache to fall back to.
  throw new OfflineCacheMissError(objectStore, args);
}

export { getRemoteOrLocal }
